<template>
  <Head :title="`Admin - Users - ${user.email} - Features`"></Head>
  <admin-user-header :user="user" />
  <s-page-block size="md" class="flex-1">
    <table class="table">
      <thead>
        <tr>
          <th>Feature</th>
          <th>Serving</th>
          <th>Override</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="feature in features" :key="feature.key">
          <td>{{ feature.label }}</td>
          <td>
            <span
              v-if="userFeatures[feature.key]"
              class="flex items-center gap-1 text-green-600 font-medium"
            >
              <s-icon name="check-circle" size="20" class="opacity-70" />
              Enabled
            </span>
            <span v-else class="flex items-center gap-1 text-red-600 font-medium">
              <s-icon name="close-circle" size="20" class="opacity-70" />
              Disabled
            </span>
          </td>
          <td class="flex gap-2">
            <s-select-field
              :id="feature.key"
              :model-value="getFeatureOverrideValue(feature.key)"
              @update:model-value="
                router.put(route('admin.users.features.update', [user.id, feature.key]), {
                  value: Boolean(~~$event),
                })
              "
            >
              <option :value="1">Enabled</option>
              <option :value="0">Disabled</option>
              <option v-if="!getFeatureOverride(feature.key)" value="default">–- None --</option>
            </s-select-field>
            <s-tooltip v-if="getFeatureOverride(feature.key)">
              <template #tooltip>
                Delete override.
                <br />
                Reset to default value.
              </template>

              <s-btn
                color="danger-white"
                as="button"
                :href="route('admin.users.features.destroy', [user.id, feature.key])"
                method="delete"
                class="w-fit"
              >
                <s-icon name="trash-can" size="20" />
              </s-btn>
            </s-tooltip>
          </td>
        </tr>
      </tbody>
    </table>
  </s-page-block>
</template>

<script setup lang="ts">
import {Head, router, useForm} from '@inertiajs/vue3';
import SPageHeader from '../../../design-system/SPageHeader.vue';
import SIcon from '../../../design-system/SIcon.vue';
import SBtn from '../../../design-system/SBtn.vue';
import AdminUserHeader from '../../../layouts/AdminUserHeader.vue';
import SPageBlock from '../../../design-system/SPageBlock.vue';
import {User} from '../../../types/entities/user';
import {route} from 'ziggy-js';
import SSelectField from '../../../design-system/SSelectField.vue';
import STooltip from '../../../design-system/STooltip.vue';
import FeatureData = App.DTOs.FeatureData;

type Role = {
  id: number;
  name: string;
};

const props = defineProps<{
  user: User;
  features: FeatureData[];
  userFeatures: {
    [key: string]: string | boolean;
  };
  userFeatureOverrides: {
    name: string;
    value: boolean;
  }[];
}>();

const getFeatureOverride = (key: string) => {
  return props.userFeatureOverrides.find((o) => o.name === key);
};

const getFeatureOverrideValue = (featureKey: string) => {
  const override = getFeatureOverride(featureKey);

  if (override) {
    return override.value ? 1 : 0;
  }

  return 'default';
};
</script>
